import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiInfinity, mdiRobotHappy } from "@mdi/js";
import { Carousel, Chat, VoiceCallModal } from "components";
import { useAccount, useAssistantMessages, isSTTSupported } from "shared/hooks";

import styles from "./styles.module.scss";
import { MainLayout } from "layouts";
import { CircularProgress } from "@mui/material";
import { ActiveToolsIndicator } from "../components/ActiveToolsIndicator";

const WELCOME_SUGGESTIONS = (length: number = 4) => {
	const itens: string[] = [
		"Quero criar conteúdo para redes sociais",
		"Preciso de ajuda com marketing digital",
		"Quero montar uma equipe de desenvolvimento",
		"Preciso de suporte para atendimento ao cliente",
		"Quero organizar meus projetos com IA",
		"Quero melhorar minha produtividade com ferramentas digitais",
		"Quero aprender novas habilidades com IA",
		"Preciso de ajuda para análise de dados",
		"Quero criar um assistente pessoal para minha rotina",
		"Quero automatizar tarefas repetitivas do meu trabalho",
	];

	const result: string[] = [];

	for (let i = 0; i < length; i++) {
		if (itens.length === 0) break;
		const index = Math.round(Math.random() * (itens.length - 1));
		result.push(itens[index]);
		itens.splice(index, 1);
	}

	return result;
};

interface AssistantChatContainerProps {
	/** Quando true, renderiza sem container/glow/header (para embutir em outra página) */
	embedded?: boolean;
	/** ID do workspace para contexto do assistente */
	workspaceId?: string | null;
}

export const AssistantChatContainer: React.FC<AssistantChatContainerProps> = ({ embedded, workspaceId }) => {
	const user = useAccount();
	const location = useLocation();

	const { messages, isStreaming, loadingChat, activeTools, sendMessage, handleConfirmation, pendingConfirmationId } = useAssistantMessages(
		workspaceId ?? null,
	);

	const hasMessages = messages.length > 0;
	const isInitialSetup = /^\/?initial-setup/i.test(location.pathname);
	const hasPendingConfirmation = messages.some((msg) => msg.senderType === "confirmation" && msg.metadata?.status === "pending");

	// EK-442: modo de voz no chat do Assistente (só em navegadores com suporte).
	const [voiceOpen, setVoiceOpen] = useState(false);
	const voiceSupported = isSTTSupported();

	// Rola até o card de confirmação pendente sempre que ele aparece/muda
	const prevPendingIdRef = useRef<string | null>(null);
	useEffect(() => {
		if (pendingConfirmationId && pendingConfirmationId !== prevPendingIdRef.current) {
			prevPendingIdRef.current = pendingConfirmationId;
			setTimeout(() => {
				document.querySelector(`[data-msg-id="${pendingConfirmationId}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" });
			}, 100);
		}
	}, [pendingConfirmationId]);

	const chatContent = (
		<>
			<div className={styles.chatArea}>
				<div className={styles.chatWrapper}>
					<Chat
						onSend={sendMessage}
						placeholder="Descreva o que você precisa..."
						disabled={hasPendingConfirmation}
						onVoiceCall={voiceSupported ? () => setVoiceOpen(true) : undefined}
					>
						{loadingChat && (
							<div className={styles.welcomeState}>
								<CircularProgress
									color="inherit"
									size={24}
								/>
								<span>Carregando conversa...</span>
							</div>
						)}

						{!hasMessages && !loadingChat && (
							<div className={styles.welcomeState}>
								<div className={styles.welcomeAvatar}>
									<Icon
										path={mdiRobotHappy}
										size={2}
									/>
								</div>
								<h2 className={styles.welcomeTitle}>Olá{user?.firstName ? `, ${user.firstName}` : ""}! Sou seu Assistente.</h2>
								<p className={styles.welcomeSubtitle}>
									Estou aqui para ajudar você a configurar seus workspaces e equipes de IA. Me conte o que você precisa e eu vou
									montar a equipe ideal para você.
								</p>
								<Carousel className={styles.suggestionsContainer}>
									{WELCOME_SUGGESTIONS(6).map((suggestion, index) => (
										<button
											key={index}
											className={styles.suggestionCard}
											onClick={() => sendMessage(suggestion)}
											disabled={isStreaming}
										>
											{suggestion}
										</button>
									))}
								</Carousel>
							</div>
						)}

						{messages.map((msg) => {
							if (msg.senderType === "confirmation" && msg.metadata) {
								return (
									<div key={msg.id} data-msg-id={msg.id}>
										<Chat.Confirmation
											toolName={msg.metadata.toolCall?.name || "tool"}
											description={msg.content || "Deseja executar esta ferramenta?"}
											status={msg.metadata.status || "pending"}
											disabled={isStreaming}
											onConfirm={(approved) => handleConfirmation(msg.metadata!.confirmationId, approved)}
										/>
									</div>
								);
							}
							if (msg.senderType === "oauth_action" && msg.metadata?.oauth) {
								const oauth = msg.metadata.oauth as {
									memberName?: string;
									teamName?: string;
									items: Array<{ slug: string; displayName: string; contractedMcpId?: string | null; envName?: string | null }>;
								};
								return (
									<Chat.OAuthAction
										key={msg.id}
										memberName={oauth.memberName}
										teamName={oauth.teamName}
										items={oauth.items}
										disabled={isStreaming}
									/>
								);
							}
							return (
								<Chat.Message
									key={msg.id}
									id={msg.id}
									incoming={msg.senderType === "assistente"}
									outgoing={msg.senderType === "user"}
									avatarUrl={msg.senderType === "assistente" ? undefined : (user?.profilePictureUrl ?? undefined)}
									avatarFallbackIconPath={msg.senderType === "assistente" ? mdiRobotHappy : undefined}
									senderName={msg.senderType === "assistente" ? "Assistente" : user?.firstName || "Você"}
									thinking={msg.thinking}
									pending={msg.pending}
									error={msg.error ? msg.errorMessage || "Ocorreu um erro." : undefined}
									isStreaming={msg.isStreaming}
									attachments={msg.attachments}
									errorAlert={msg.metadata?.error_alert}
								>
									{msg.content || null}
								</Chat.Message>
							);
						})}

						<ActiveToolsIndicator activeTools={activeTools} />
					</Chat>
				</div>
			</div>

			{voiceSupported && (
				<VoiceCallModal
					open={voiceOpen}
					onClose={() => setVoiceOpen(false)}
					leaderName="Assistente"
					leaderAvatarChar="A"
					sendMessage={sendMessage}
					messages={messages}
					isStreaming={isStreaming}
					replySenderType="assistente"
					onConfirm={handleConfirmation}
				/>
			)}
		</>
	);

	if (embedded) {
		return chatContent;
	}

	return (
		<div className={styles.container}>
			{/* Background Glow */}
			<div className={styles.radialGlow} />

			{/* Header */}
			{isInitialSetup && (
				<header className={styles.header}>
					<div className={styles.headerContent}>
						<div className={styles.logo}>
							<div className={styles.logoIcon}>
								<Icon
									path={mdiInfinity}
									size={0.9}
								/>
							</div>
							<span className={styles.logoText}>e-Keep</span>
						</div>
						<span className={styles.headerTitle}>Líder Global</span>
						<RouterLink
							to="/"
							className={styles.skipButton}
						>
							Configurar depois
						</RouterLink>
					</div>
				</header>
			)}

			{chatContent}
		</div>
	);
};
